import React, { useState, useMemo } from 'react';
import { clipTopicName } from '../../utils/topicUtils';
import { getIndustryColor, getCategoryPalette } from '../../utils/colorConstants';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ErrorBoundary from '../ErrorBoundary';
import './SummaryPanel.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function SummaryPanel({ data, statistics, networkData, themeColors, theme, schemaConfig, onHelpSection }) {
  // Schema-driven labels (fall back to MaxIV terminology).
  const labels = schemaConfig?.labels || {};
  const pluralize = (s) => {
    if (!s) return s;
    if (/s$/i.test(s)) return s;            // already plural-ish (e.g. "Topics")
    if (/y$/i.test(s)) return s.slice(0, -1) + 'ies'; // Industry -> Industries
    return s + 's';
  };
  const industryLabel = pluralize(labels.industry || 'Industry');
  const topicLabel = pluralize(labels.topic || 'Topic');
  const recordLabel = (labels.record || 'Proposal').toLowerCase();
  // Beamline/affiliation are MaxIV network concepts; only show when present.
  const hasBeamlines = !!(data?.beamlines && data.beamlines.length);
  const hasAffiliations = !!(data?.affiliations && data.affiliations.length);
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    beamlines: true,
    affiliations: true,
    industries: true,
    topics: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const expandAll = () => {
    setExpandedSections({
      beamlines: true,
      affiliations: true,
      industries: true,
      topics: true
    });
  };

  const collapseAll = () => {
    setExpandedSections({
      beamlines: false,
      affiliations: false,
      industries: false,
      topics: false
    });
  };

  const allExpanded = Object.values(expandedSections).every(Boolean);
  const allCollapsed = Object.values(expandedSections).every(v => !v);

  // Calculate how many items to show based on expanded sections
  const expandedCount = Object.values(expandedSections).filter(Boolean).length;
  const maxItemsPerChart = expandedCount <= 1 ? 20 : expandedCount === 2 ? 15 : 10;

  // Process data for charts
  const chartData = useMemo(() => {
    if (!data) return null;

    const processDistribution = (items, maxItems, colorFnOrPalette, totalUnique) => {
      if (!items || !Array.isArray(items) || items.length === 0) return null;
      
      // Data already comes as [{label, count}] from transformSummaryData
      // Sort by count descending and take top N based on available space
      const sorted = [...items]
        .filter(item => item && item.label && typeof item.count === 'number')
        .sort((a, b) => b.count - a.count)
        .slice(0, maxItems);

      if (sorted.length === 0) return null;

      // Use per-item colorFn when provided (e.g. fixed industry colors),
      // palette array for category-specific assignment,
      // or fall back to brand chart palette
      let bgColors;
      if (typeof colorFnOrPalette === 'function') {
        bgColors = sorted.map(item => colorFnOrPalette(item.label));
      } else if (Array.isArray(colorFnOrPalette)) {
        bgColors = sorted.map((_, i) => colorFnOrPalette[i % colorFnOrPalette.length]);
      } else {
        const fallback = themeColors?.charts?.set1 || ['#888'];
        bgColors = sorted.map((_, i) => fallback[i % fallback.length]);
      }
      const borderColors = bgColors.map(c => {
        // darken by 15% for border
        const hex = c.replace('#', '');
        const r = Math.max(0, Math.round(parseInt(hex.substring(0, 2), 16) * 0.85));
        const g = Math.max(0, Math.round(parseInt(hex.substring(2, 4), 16) * 0.85));
        const b = Math.max(0, Math.round(parseInt(hex.substring(4, 6), 16) * 0.85));
        return `rgb(${r},${g},${b})`;
      });

      return {
        labels: sorted.map(item => clipTopicName(String(item.label || ''))),
        fullLabels: sorted.map(item => String(item.label || '')),
        datasets: [{
          data: sorted.map(item => item.count),
          backgroundColor: bgColors,
          borderColor: borderColors,
          borderWidth: 1
        }],
        totalCount: totalUnique != null ? totalUnique : items.length
      };
    };

    // Fixed color lookup for industries — same color in every panel
    const industryColorFn = (label) => getIndustryColor(label, theme);

    // Category-specific palettes — each category has its own hue family
    const beamlinePalette    = getCategoryPalette('beamline',    theme);
    const affiliationPalette = getCategoryPalette('affiliation', theme);
    const topicPalette       = getCategoryPalette('topic',       theme);

    return {
      beamlines:    processDistribution(data.beamlines,    maxItemsPerChart, beamlinePalette,    data.totalUniqueBeamlines),
      affiliations: processDistribution(data.affiliations, maxItemsPerChart, affiliationPalette, data.totalUniqueAffiliations),
      industries:   processDistribution(data.industries,   maxItemsPerChart, industryColorFn,    data.totalUniqueIndustries),
      topics:       processDistribution(data.topics,       maxItemsPerChart, topicPalette,       data.totalUniqueTopics)
    };
  }, [data, themeColors, maxItemsPerChart, theme]);

  // Memoize chart options to prevent re-creation on every render
  const chartOptions = useMemo(() => ({
    indexAxis: 'y', // Horizontal bar chart
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 0,
        right: 8
      }
    },
    animation: {
      duration: 0 // Disable animations to prevent render conflicts
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (context) => {
            // Show full label in tooltip
            const idx = context?.[0]?.dataIndex;
            const chart = context?.[0]?.chart;
            if (idx !== undefined && chart?.data?.labels) {
              return chart.data.labels[idx];
            }
            return '';
          },
          label: (context) => {
            const value = context?.raw;
            return `Count: ${value !== undefined && value !== null ? value : 0}`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: true,
          color: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          drawBorder: false
        },
        ticks: {
          precision: 0,
          color: themeColors?.text?.muted || '#757575'
        }
      },
      y: {
        grid: {
          display: false
        },
        afterFit: (axis) => {
          // Give at least 40% of chart width to labels
          const chartWidth = axis.chart.width;
          axis.width = Math.max(axis.width, chartWidth * 0.4);
        },
        ticks: {
          color: themeColors?.text?.dark || '#424242',
          font: {
            size: 11
          },
          autoSkip: false,
          maxRotation: 0,
          callback: function(value) {
            const label = this.getLabelForValue(value);
            if (!label) return '';
            // Dynamically truncate based on available axis width
            const maxChars = Math.max(15, Math.floor(this.chart.width / 12));
            return label.length > maxChars ? label.substring(0, maxChars - 1) + '…' : label;
          }
        }
      }
    }
  }), [themeColors]);

  if (!data) {
    return (
      <div className={`summary-panel theme-${theme}`}>
        <div className="summary-panel-header">
          <h2>Summary</h2>
        </div>
        <div className="summary-empty">
          <p>No data available. Apply filters and run analysis to see summary.</p>
        </div>
      </div>
    );
  }

  const renderSection = (title, sectionKey, chartDataForSection, icon) => {
    const isExpanded = expandedSections[sectionKey];
    const hasData = chartDataForSection && chartDataForSection.labels.length > 0;
    const displayedCount = hasData ? chartDataForSection.labels.length : 0;
    const totalCount = hasData ? (chartDataForSection.totalCount || displayedCount) : 0;
    
    // Dynamic height based on number of items shown
    const chartHeight = displayedCount * 28 + 20; // ~28px per bar + padding

    return (
      <div className={`summary-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div 
          className="summary-section-header"
          onClick={() => toggleSection(sectionKey)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && toggleSection(sectionKey)}
        >
          <span className="section-icon">{icon}</span>
          <h3>{title}</h3>
          <span className="section-count">
            {hasData ? (displayedCount < totalCount ? `(${displayedCount}/${totalCount})` : `(${totalCount})`) : '(0)'}
          </span>
          <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>▼</span>
        </div>
        {isExpanded && (
          <div className="summary-section-content">
            {hasData ? (
              <div className="chart-container" style={{ height: `${chartHeight}px` }}>
                <ErrorBoundary fallbackMessage="Chart failed to render">
                  <Bar data={chartDataForSection} options={chartOptions} />
                </ErrorBoundary>
              </div>
            ) : (
              <p className="no-data-message">No data available</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Calculate node counts
  // Org count comes from the network graph (what the user actually sees),
  // since org_entities in the raw data can include orgs filtered out during
  // network analysis.  Beamline/affiliation use totalUnique* from the backend
  // because those are the true totals behind the chart headers.
  // MaxIV-style schemas surface an "orgs" count; schema-driven multilayer
  // graphs (e.g. MARVIN) show the total visible node count instead, so it
  // tracks toggles like "require Excellence Cluster".
  const isMaxIVSummary = hasBeamlines || hasAffiliations;
  const orgCountFromNetwork = networkData?.nodes
    ? (isMaxIVSummary
        ? networkData.nodes.filter(n => n.type === 'Organization' || !n.id.includes('_')).length
        : networkData.nodes.length)
    : 0;

  const nodeCountsFromData = data ? {
    orgs: orgCountFromNetwork,
    beamlines: data.totalUniqueBeamlines || data.beamlines?.length || 0,
    affiliations: data.totalUniqueAffiliations || data.affiliations?.length || 0
  } : null;

  const nodeCountsFromNetwork = networkData?.nodes ? {
    orgs: orgCountFromNetwork,
    beamlines: networkData.nodes.filter(n => n.id.includes('_Beamline')).length,
    affiliations: networkData.nodes.filter(n => n.id.includes('_Affiliation')).length
  } : { orgs: 0, beamlines: 0, affiliations: 0 };

  // Use node-specific counts if available (when totalProposals is set), otherwise use network counts
  const nodeCounts = (data?.totalProposals !== undefined && nodeCountsFromData) 
    ? nodeCountsFromData 
    : nodeCountsFromNetwork;

  const proposalCount = data?.totalProposals !== undefined 
    ? data.totalProposals 
    : (statistics?.Proposals || 0);

  return (
    <div className={`summary-panel theme-${theme}`}>
      <div className="summary-panel-header">
        <div className="header-left">
          <div className="summary-title-row">
            <h2>Summary</h2>
            {onHelpSection && (
              <button
                className="help-info-icon"
                onClick={() => onHelpSection('summary')}
                data-tooltip="" title="Learn about Summary"
              >
                ⓘ
              </button>
            )}
          </div>
          <div className="network-stats-compact">
            <span className="stat-item-compact">
              {proposalCount} {pluralize(recordLabel)}
            </span>
            {nodeCounts.orgs > 0 && (
              <>
                <span className="stat-separator">•</span>
                <span className="stat-item-compact">
                  {nodeCounts.orgs} {isMaxIVSummary ? 'orgs' : 'nodes'}
                </span>
              </>
            )}
            {hasBeamlines && (
              <>
                <span className="stat-separator">•</span>
                <span className="stat-item-compact">
                  {nodeCounts.beamlines} beamlines
                </span>
              </>
            )}
            {hasAffiliations && (
              <>
                <span className="stat-separator">•</span>
                <span className="stat-item-compact">
                  {nodeCounts.affiliations} affiliations
                </span>
              </>
            )}
          </div>
        </div>
        <div className="header-controls">
          <div className="expand-collapse-buttons">
            <button 
              className="expand-collapse-btn"
              onClick={expandAll}
              disabled={allExpanded}
              title="Expand All"
            >
              ⊞
            </button>
            <button 
              className="expand-collapse-btn"
              onClick={collapseAll}
              disabled={allCollapsed}
              title="Collapse All"
            >
              ⊟
            </button>
          </div>
        </div>
      </div>
      
      <div className="summary-sections">
        {renderSection(industryLabel, 'industries', chartData?.industries, '🏭')}
        {renderSection(topicLabel, 'topics', chartData?.topics, '🏷️')}
        {hasBeamlines && renderSection('Beamlines', 'beamlines', chartData?.beamlines, '🔬')}
        {hasAffiliations && renderSection('Affiliations', 'affiliations', chartData?.affiliations, '🏛️')}
      </div>
    </div>
  );
}

export default SummaryPanel;
